import React, { useState, useEffect, useCallback, useRef } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  useAddCompanyCertificationMutation,
  useCompanyCertificationsQuery,
  useGetCertificationsLazyQuery,
  useRemoveCompanyCertificationMutation,
  useVerifyCompanyCertificationMutation,
} from '../../../gql/generated/graphql';
import { useCreateChatSession, useChatMessages, useSendMessage, useChatSessions } from '../../../hooks/useChatApi';
import { useTenant } from '../../../contexts/tenant-context';
import { useSnackbar } from '../../../providers/SnackbarProvider';
import type { ChatMessage } from '../../../types/chat';

interface Props {
  companyId: number;
  companyName?: string;
}

const CertificationsStep = ({ companyId, companyName }: Props) => {
  const { showSnackbar } = useSnackbar();
  const { selectedTenantId: tenantId } = useTenant();
  const userId = 1; // TODO: derive from auth context
  const [searchValue, setSearchValue] = useState('');

  // ---- Certifications DataGrid ----
  const { data, loading, refetch } = useCompanyCertificationsQuery({
    variables: { companyId },
  });

  const [searchCertifications, { data: searchData, loading: searchLoading }] = useGetCertificationsLazyQuery();
  const [addCertification] = useAddCompanyCertificationMutation();
  const [removeCertification] = useRemoveCompanyCertificationMutation();
  const [verifyCertification] = useVerifyCompanyCertificationMutation();

  const certifications = data?.companyCertifications || [];
  const searchResults = searchData?.certifications || [];

  // ---- Chat session management ----
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: sessionsData } = useChatSessions(userId, tenantId, 50, 0, 'certification', companyId);

  useEffect(() => {
    if (sessionsData?.sessions?.length && !sessionId) {
      setSessionId(sessionsData.sessions[0].id);
    }
  }, [sessionsData, sessionId]);

  const createSession = useCreateChatSession();

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    const session = await createSession.mutateAsync({
      userId,
      tenantId: tenantId ?? 1,
      agentType: 'certification',
      companyId,
      companyName: companyName || `Company #${companyId}`,
    });
    setSessionId(session.id);
    return session.id;
  }, [sessionId, createSession, userId, tenantId, companyId, companyName]);

  const { data: messagesData } = useChatMessages(userId, tenantId, sessionId);
  const messages: ChatMessage[] = messagesData?.messages || [];

  const sendMessage = useSendMessage();

  const handleSend = useCallback(async () => {
    const content = inputValue.trim();
    if (!content || sendMessage.isPending) return;

    setInputValue('');

    try {
      const sid = await ensureSession();
      await sendMessage.mutateAsync({
        userId,
        tenantId: tenantId ?? 1,
        sessionId: sid,
        content,
      });
      refetch();
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  }, [inputValue, sendMessage, ensureSession, userId, tenantId, refetch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessage.isPending]);

  // ---- Handlers ----
  const handleVerify = async (id: number, isVerified: boolean) => {
    const result = await verifyCertification({ variables: { id, isVerified } });
    if (result.data?.verifyCompanyCertification?.success) {
      refetch();
    }
  };

  const handleRemove = async (id: number) => {
    const result = await removeCertification({ variables: { id } });
    if (result.data?.removeCompanyCertification?.success) {
      showSnackbar('Certification removed', 'success');
      refetch();
    }
  };

  const handleAdd = async (certificationId: number) => {
    const result = await addCertification({ variables: { companyId, certificationId } });
    if (result.data?.addCompanyCertification?.success) {
      showSnackbar('Certification added', 'success');
      refetch();
      setSearchValue('');
    } else {
      showSnackbar(result.data?.addCompanyCertification?.message || 'Failed to add', 'error');
    }
  };

  // ---- DataGrid columns ----
  const columns: GridColDef[] = [
    { field: 'certificationName', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'certificationIssuer', headerName: 'Issuer', width: 130 },
    {
      field: 'certificationDomain',
      headerName: 'Domain',
      width: 110,
      renderCell: params => (params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-'),
    },
    {
      field: 'certificationLevel',
      headerName: 'Level',
      width: 100,
      renderCell: params => (params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-'),
    },
    {
      field: 'certificationAiMoatLevel',
      headerName: 'AI Moat',
      width: 90,
      renderCell: params => {
        const level = params.value;
        if (!level) return '-';
        const color = level === 'high' ? 'success' : level === 'medium' ? 'warning' : 'default';
        return <Chip label={level} size="small" color={color as any} />;
      },
    },
    {
      field: 'extractionMethod',
      headerName: 'Source',
      width: 90,
      renderCell: params => <Chip label={params.value || '-'} size="small" variant="outlined" />,
    },
    {
      field: 'extractionConfidence',
      headerName: 'Conf.',
      width: 70,
      renderCell: params => {
        const val = params.value;
        if (val == null) return '-';
        return <Typography variant="caption">{Math.round(val * 100)}%</Typography>;
      },
    },
    {
      field: 'isVerified',
      headerName: 'Verified',
      width: 70,
      renderCell: params => (
        <Checkbox
          checked={params.value || false}
          onChange={e => handleVerify(params.row.id, e.target.checked)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: params => (
        <Tooltip title="Remove certification">
          <IconButton size="small" onClick={() => handleRemove(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left: Certifications DataGrid */}
        <Grid item xs={12} md={7}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Certifications
              </Typography>
            </Box>

            <Box mb={1.5}>
              <Autocomplete
                sx={{ maxWidth: 400 }}
                options={searchResults}
                getOptionLabel={(opt: any) => `${opt.name} (${opt.issuer || 'unknown issuer'})`}
                loading={searchLoading}
                inputValue={searchValue}
                onInputChange={(_, value) => {
                  setSearchValue(value);
                  if (value.length >= 2) {
                    searchCertifications({ variables: { search: value } });
                  }
                }}
                onChange={(_, value: any) => {
                  if (value?.dbId) handleAdd(value.dbId);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Add certification"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <AddIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                    }}
                  />
                )}
                size="small"
              />
            </Box>

            <DataGrid
              rows={certifications}
              getRowId={row => row.id}
              columns={columns}
              loading={loading}
              autoHeight
              disableRowSelectionOnClick
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10]}
              sx={{
                fontFamily: 'var(--font-data)',
                fontSize: 'var(--text-sm)',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid var(--border-subtle)',
                },
              }}
            />
          </Paper>
        </Grid>

        {/* Right: Chat Panel */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 0,
              height: 520,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Chat header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyIcon fontSize="small" color="primary" />
                Certification Manager
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Search, add, or create certifications via chat
              </Typography>
            </Box>

            {/* Messages area */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                px: 2,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.length === 0 && !sendMessage.isPending && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    opacity: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Ask about certifications — search, add existing ones, or create new entries.
                    <br />
                    The agent will search the database and web for accurate details.
                  </Typography>
                </Box>
              )}

              {messages.map(msg => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: msg.role === 'user' ? 'primary.main' : 'action.selected',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    {msg.role === 'user' ? (
                      <PersonIcon sx={{ fontSize: 16, color: 'primary.contrastText' }} />
                    ) : (
                      <SmartToyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                  </Box>
                  <Box
                    sx={{
                      maxWidth: '85%',
                      backgroundColor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                      color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      fontSize: '0.8125rem',
                      lineHeight: 1.5,
                      '& p': { m: 0 },
                      '& p + p': { mt: 0.5 },
                      '& ul, & ol': { m: 0, pl: 2 },
                      '& table': {
                        borderCollapse: 'collapse',
                        fontSize: '0.75rem',
                        mt: 0.5,
                        '& td, & th': {
                          border: '1px solid',
                          borderColor: 'divider',
                          px: 0.5,
                          py: 0.25,
                        },
                      },
                      '& code': {
                        backgroundColor: 'action.selected',
                        borderRadius: 0.5,
                        px: 0.5,
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </Box>
                </Box>
              ))}

              {sendMessage.isPending && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'action.selected',
                      flexShrink: 0,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Box>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <TextField
                fullWidth
                multiline
                maxRows={3}
                size="small"
                placeholder="e.g. Add ISO 27001 or search for SOC certifications..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sendMessage.isPending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8125rem',
                    borderRadius: 2,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!inputValue.trim() || sendMessage.isPending}
                size="small"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CertificationsStep;
